import { clsx } from "clsx"

import { batteryConcertErrorMessage } from "~/api/battery-concert-application-errors"
import type { BatterySupplementalStatusDto } from "~/api/dto/battery-supplemental.dto"
import {
  useBatteryConcertSupplemental,
  useResendBatteryConcertSupplemental,
  useRetryBatteryConcertSupplemental,
  useSendBatteryConcertSupplemental,
} from "~/api/hooks/useBatteryConcertSupplemental"

import {
  concertDateLabel,
  OUTCOME_TONE_CLASS,
  RESEND_SUPPLEMENTAL_BUSY_LABEL,
  RESEND_SUPPLEMENTAL_LABEL,
  RETRY_SUPPLEMENTAL_BUSY_LABEL,
  RETRY_SUPPLEMENTAL_LABEL,
  SEND_SUPPLEMENTAL_BUSY_LABEL,
  SEND_SUPPLEMENTAL_LABEL,
  SUPPLEMENTAL_DOCUMENTS_HEADING,
  SUPPLEMENTAL_NO_PACKAGE_DETAIL,
  SUPPLEMENTAL_NOT_STARTED_DETAIL,
  SUPPLEMENTAL_CLOSED_DETAIL,
  SUPPLEMENTAL_SIGNED_DETAIL,
  SUPPLEMENTAL_STATUS_COPY,
} from "./concert-battery-copy"
import {
  ConcertSection,
  concertPrimaryButtonClass,
  concertSecondaryButtonClass,
} from "./concert-battery-section"

const UNKNOWN_STATE = {
  label: `Unknown`,
  detail: `Vision does not recognise the state of this supplemental package. Contact support.`,
  tone: `muted`,
} as const

const describe = (
  status: BatterySupplementalStatusDto,
): { label: string; detail: string; tone: keyof typeof OUTCOME_TONE_CLASS } => {
  const { attempt } = status

  if (!attempt) {
    return status.mainPackageRef
      ? {
          label: `Not sent`,
          detail: SUPPLEMENTAL_NOT_STARTED_DETAIL,
          tone: `muted`,
        }
      : {
          label: `Waiting`,
          detail: SUPPLEMENTAL_NO_PACKAGE_DETAIL,
          tone: `muted`,
        }
  }

  if (attempt.dateSigned) {
    return {
      label: `Signed`,
      detail: SUPPLEMENTAL_SIGNED_DETAIL,
      tone: `success`,
    }
  }

  // An envelope DocuSign closed without a signature is neither sent nor
  // failed, and reading "Sent for signature" for one would be wrong.
  if (attempt.envelopeClosedStatus) {
    return {
      label:
        attempt.envelopeClosedStatus.charAt(0).toUpperCase() +
        attempt.envelopeClosedStatus.slice(1),
      detail:
        SUPPLEMENTAL_CLOSED_DETAIL[attempt.envelopeClosedStatus] ??
        `The supplemental envelope is closed. Contact support before sending these documents again.`,
      tone: `danger`,
    }
  }

  return SUPPLEMENTAL_STATUS_COPY.get(attempt.state) ?? UNKNOWN_STATE
}

/**
 * Historical supplemental packages sent separately before Powur primary contracts.
 *
 * Its own card, below Concert's: they are two envelopes with two signatures,
 * and a seller reading one must not mistake it for the other. Every action
 * shown is one the server said is possible right now — nothing here decides
 * eligibility, and reading this never sends anything.
 */
export function ConcertBatterySupplementalDocuments({
  proposalId,
}: {
  proposalId: string
}): JSX.Element | null {
  const query = useBatteryConcertSupplemental(proposalId)
  const send = useSendBatteryConcertSupplemental()
  const retry = useRetryBatteryConcertSupplemental()
  const resend = useResendBatteryConcertSupplemental()

  const status = query.data
  if (!status || (!status.attempt && !status.actions.canSendInitial)) {
    return null
  }

  const { attempt, actions } = status
  const copy = describe(status)
  const pending = send.isLoading || retry.isLoading || resend.isLoading
  const actionError = send.error ?? retry.error ?? resend.error

  return (
    <ConcertSection
      title={SUPPLEMENTAL_DOCUMENTS_HEADING}
      testId="battery-concert-supplemental-documents"
      trailing={
        <span
          data-testid="battery-concert-supplemental-status"
          className={clsx(
            `inline-flex items-center rounded-full border px-2.5 py-0.5 text-[length:var(--step--2)] font-semibold`,
            OUTCOME_TONE_CLASS[copy.tone],
          )}
        >
          {copy.label}
        </span>
      }
    >
      <div className="flex flex-col items-start gap-3 px-4 py-4">
        {/* The server's own reason replaces the generic line rather than
            following it: the two say the same thing, and stacking them reads
            as a stutter. */}
        {attempt?.failureMessage == null ? (
          <p className="text-[length:var(--step--1)] text-bp-text-muted">
            {copy.detail}
            {attempt?.dateSent != null
              ? ` Sent ${concertDateLabel(attempt.dateSent)}.`
              : ``}
            {attempt?.dateSigned != null
              ? ` Signed ${concertDateLabel(attempt.dateSigned)}.`
              : ``}
          </p>
        ) : (
          <p
            role="alert"
            data-testid="battery-concert-supplemental-reason"
            className="text-[length:var(--step--2)] text-bp-danger"
          >
            {attempt.failureMessage}
          </p>
        )}

        {/* Only a package that actually went out names its documents. Listing
            them under "Send failed" or "Outcome unknown" would suggest the
            homeowner has them, which is the one thing this card exists to keep
            straight. */}
        {attempt?.envelopeId != null && attempt.documents.length > 0 ? (
          <ul
            data-testid="battery-concert-supplemental-document-list"
            className="flex list-disc flex-col gap-1 pl-5 text-[length:var(--step--2)] text-bp-text-muted"
          >
            {attempt.documents.map((name, index) => (
              // Two applicable templates can share a display name, so the
              // position in the package is what makes the key unique.
              <li key={`${index}-${name}`}>{name}</li>
            ))}
          </ul>
        ) : null}

        {actionError != null ? (
          <p
            role="alert"
            className="text-[length:var(--step--2)] text-bp-danger"
          >
            {batteryConcertErrorMessage(actionError) ??
              `Unable to complete that request. Try again.`}
          </p>
        ) : null}

        <div className="flex flex-wrap items-center gap-3">
          {actions.canSendInitial ? (
            <button
              type="button"
              disabled={pending}
              onClick={() => send.mutate({ proposalId })}
              data-testid="battery-concert-supplemental-send"
              className={concertPrimaryButtonClass}
            >
              {send.isLoading
                ? SEND_SUPPLEMENTAL_BUSY_LABEL
                : SEND_SUPPLEMENTAL_LABEL}
            </button>
          ) : null}
          {actions.canRetrySend && attempt != null ? (
            <button
              type="button"
              disabled={pending}
              onClick={() => retry.mutate({ proposalId, attemptId: attempt.id })}
              data-testid="battery-concert-supplemental-retry"
              className={concertPrimaryButtonClass}
            >
              {retry.isLoading
                ? RETRY_SUPPLEMENTAL_BUSY_LABEL
                : RETRY_SUPPLEMENTAL_LABEL}
            </button>
          ) : null}
          {actions.canResend ? (
            <button
              type="button"
              disabled={pending}
              onClick={() => resend.mutate({ proposalId })}
              data-testid="battery-concert-supplemental-resend"
              className={concertSecondaryButtonClass}
            >
              {resend.isLoading
                ? RESEND_SUPPLEMENTAL_BUSY_LABEL
                : RESEND_SUPPLEMENTAL_LABEL}
            </button>
          ) : null}
        </div>
      </div>
    </ConcertSection>
  )
}
